<template>
<div class="suggestion-actions">
    <span
        v-if="isBroadcaster"
        @click="toggleReply"
        :data-tippy-content="replyText"
        class="icon">
        <FaLayers fixedWidth>
            <FaIcon icon="reply" class="has-text-link"/>
            <FaIcon v-show="isReplying" icon="ban" class="has-text-danger"/>
        </FaLayers>
    </span>
    <!-- The completion state is sealed after a confirmation.
        This is a client side only check, but who cares really. -->
    <span
        v-if="canComplete"
        @click="toggleCompleted"
        :data-tippy-content="completedText"
        class="icon">
        <Loader v-if="isUpdatingCompleted" />
        <FaLayers v-else fixedWidth>
            <FaIcon icon="trophy" class="has-text-warning"/>
            <FaIcon v-show="isCompleted" icon="ban" class="has-text-danger"/>
        </FaLayers>
    </span>
    <span
        v-if="canApprove"
        @click="toggleApprove"
        :data-tippy-content="approveText"
        class="icon">
        <Loader v-if="isLoading" />
        <FaLayers v-else fixedWidth>
            <FaIcon icon="check" class="has-text-success"/>
            <FaIcon v-show="isApproved" icon="ban" class="has-text-danger"/>
        </FaLayers>
    </span>
    <span
        v-if="isBroadcaster"
        @click="toggleArchive"
        :data-tippy-content="archiveText"
        class="icon">
        <Loader v-if="isLoading" />
        <FaLayers v-else fixedWidth>
            <FaIcon icon="archive" class="has-text-link"/>
            <FaIcon v-show="isArchived" icon="ban" class="has-text-danger"/>
        </FaLayers>
    </span>
    <span
        v-if="canDelete"
        @click="toggleDelete"
        :data-tippy-content="deleteText"
        class="icon">
        <Loader v-if="isLoading" />
        <FaLayers v-else fixedWidth>
            <FaIcon class="has-text-danger" icon="trash"/>
            <FaIcon v-show="isDeleted" icon="ban"/>
        </FaLayers>
    </span>
    <span
        v-if="isAdmin"
        data-tippy-content="Chat bans and timeouts are honored."
        class="icon">
        <FaIcon icon="gavel"/>
    </span>
</div>
</template>

<script>
import tippy, { createSingleton } from '@/tippy'
import { mapGetters } from 'vuex'

import * as C from '@shared/constants'
import * as M from '@/store/mutations'

// admins can only approve suggestions in the pending list
// admins can delete posts in any list view
export default {
    name: 'SuggestionActions',
    props: {
        suggestion: Object,
        isAdmin: Boolean,
        isOriginalPoster: Boolean,
    },
    data: vm => ({
        isLoading: false,
        isUpdatingCompleted: false,
        initialStatus: vm.suggestion.status,
        showUndo: false,
    }),
    computed: {
        ...mapGetters(['isBroadcaster', 'isPendingList', 'replyToSuggestion']),
        status: vm => vm.suggestion.status,
        canApprove: vm => {
            return vm.isPendingList || vm.initialStatus !== C.STATUS_APPROVED
        },
        isApproved: vm => vm.status === C.STATUS_APPROVED,
        approveText: vm => (vm.isApproved ? 'Undo ' : '') + 'Approve Post',
        canComplete: vm => vm.isBroadcaster && !vm.isCompletedConfirmed,
        isCompleted: vm => !!vm.suggestion.completedAt,
        isCompletedConfirmed: vm => !!vm.suggestion.completionConfirmedAt,
        completedText: vm => (vm.isCompleted ? 'Undo ' : '') + 'Mark As Completed',
        canDelete: vm => vm.isAdmin || vm.isOriginalPoster,
        isDeleted: vm => vm.status === C.STATUS_DELETED,
        deleteText: vm => (vm.isDeleted ? 'Undo ' : '') + 'Delete Post',
        isReplying: vm => vm.replyToSuggestion?.id === vm.suggestion.id,
        replyText: vm => (
            vm.isReplying // hard to read sry
                ? 'Cancel ' : vm.hasReplied
                    ? 'Edit ' : '') + 'Reply',
        hasReplied: vm => vm.suggestion.reply?.text.length > 0,
        isArchived: vm => vm.status === C.STATUS_ARCHIVED,
        archiveText: vm => (vm.isArchived ? 'Undo ' : '') + 'Archive Post',
        propsThatUpdateTippy: vm => `${vm.status}_${vm.isReplying}_${vm.isCompleted}`
    },
    watch: {
        // TODO: This is annoying. Find a library that uses popper 2.0?
        propsThatUpdateTippy: 'updateTippy'
    },
    // TODO: DRY this up with Suggestion.vue
    mounted () {
        this.updateTippy()
        this.$once('hook:destroyed', this.destroyTippy)
    },
    methods: {
        // TODO: DRY this up with Suggestion.vue
        async updateTippy () {
            this.destroyTippy()
            await this.$nextTick() // wait for rerender.

            const $refs = this.$el.querySelectorAll('[data-tippy-content]')
            this.tippyInstances = tippy($refs)
            this.singleton = createSingleton(this.tippyInstances, {
                offset: [0, 15],
                placement: 'left',
                //trigger: 'click', // DEBUG ONLY
            })
        },
        // TODO: DRY this up with Suggestion.vue
        destroyTippy () {
            /* eslint-disable no-unused-expressions */ // fuck off eslint wtf
            this.tippyInstances?.forEach(t => t.destroy())
            this.singleton?.destroy()
        },
        toggleReply () {
            const payload = { suggestion: { ...this.suggestion } }
            if (this.isReplying) {
                payload.suggestion = null
            }
            this.$store.commit(M.SET_REPLY_TO_SUGGESTION, payload)
        },
        toggleApprove () {
            return this.isApproved
                ? this.undo() : this.updateStatus(C.STATUS_APPROVED)
        },
        toggleDelete () {
            return this.isDeleted
                ? this.undo() : this.updateStatus(C.STATUS_DELETED)
        },
        toggleArchive () {
            let status
            if (this.isArchived) {
                status = this.initialStatus === C.STATUS_ARCHIVED
                    ? C.STATUS_APPROVED
                    : this.initialStatus
            } else {
                status = C.STATUS_ARCHIVED
            }
            this.updateStatus(status)
        },
        async toggleCompleted () {
            try {
                this.isUpdatingCompleted = true
                const completedAt = this.isCompleted ? null : new Date()
                const payload = { id: this.suggestion.id, completedAt }
                await this.$store.dispatch('updateSuggestionCompletedAt', payload)
            } finally {
                this.isUpdatingCompleted = false
            }
        },
        async undo () {
            await this.updateStatus(this.initialStatus)
        },
        async updateStatus (status) {
            try {
                this.isLoading = true
                const payload = { id: this.suggestion.id, status }
                await this.$store.dispatch('updateSuggestionStatus', payload)
            } finally {
                this.isLoading = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.suggestion-actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: 24px;
}

.icon {
    cursor: pointer;
}
</style>
